import { useEffect, useRef, useState } from 'react';
import { Camera, ClipboardPaste, Upload, X } from 'lucide-react';
import { subirVarios } from './subida';
import { tamano } from './formato';

/**
 * El panel de subida: varios a la vez con progreso por archivo, pegar
 * desde el portapapeles (capturas) y la cámara del teléfono.
 */
export default function Subir({ idCarpeta, tiposPermitidos = [], topeMb = 100, onTerminado, onCerrar, soltados = null }) {
  const [estados, setEstados] = useState([]);
  const [ocupado, setOcupado] = useState(false);
  const entrada = useRef(null);
  const camara = useRef(null);

  async function subir(lista) {
    const archivos = Array.from(lista || []).filter((a) => a && a.size >= 0);
    if (!archivos.length) return;
    setOcupado(true);
    const fin = await subirVarios(archivos, { idCarpeta, alCambiar: setEstados });
    setOcupado(false);
    if (fin.some((e) => e.estado === 'LISTO')) onTerminado?.(fin);
  }

  useEffect(() => {
    if (soltados?.archivos?.length) subir(soltados.archivos);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soltados]);

  useEffect(() => {
    function alPegar(ev) {
      const items = Array.from(ev.clipboardData?.items || []);
      const archivos = items.filter((i) => i.kind === 'file').map((i) => i.getAsFile()).filter(Boolean)
        .map((f, n) => (f.name && f.name !== 'image.png' ? f : new File([f], `captura-${Date.now()}-${n + 1}.png`, { type: f.type })));
      if (archivos.length) { ev.preventDefault(); subir(archivos); }
    }
    document.addEventListener('paste', alPegar);
    return () => document.removeEventListener('paste', alPegar);
  });

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4 space-y-3" data-panel="subir">
      <div className="flex items-center gap-2">
        <Upload className="h-5 w-5 text-[var(--color-marca)]" />
        <span className="font-semibold text-gray-900">Subir archivos</span>
        <button type="button" onClick={onCerrar} className="ml-auto text-gray-500 flex items-center gap-1 text-sm" aria-label="Cerrar el panel de subida">
          <X className="h-4 w-4" /> Cerrar
        </button>
      </div>
      <p className="text-xs text-gray-600">
        Hasta {topeMb} MB por archivo. Tipos: {tiposPermitidos.join(', ') || 'los de tu empresa'}. Los grandes van por partes y,
        si se corta la red, siguen donde iban. También puedes pegar una captura (Ctrl+V).
      </p>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => entrada.current?.click()} disabled={ocupado}
                className="bg-[var(--color-marca)] text-white font-semibold px-4 py-2.5 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60">
          <Upload className="h-4 w-4" /> Elegir archivos
        </button>
        <button type="button" onClick={() => camara.current?.click()} disabled={ocupado}
                className="border border-gray-300 text-gray-800 font-semibold px-4 py-2.5 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60">
          <Camera className="h-4 w-4" /> Tomar foto
        </button>
        <span className="text-xs text-gray-500 self-center flex items-center gap-1"><ClipboardPaste className="h-3.5 w-3.5" /> o pega una captura</span>
        <input ref={entrada} type="file" multiple className="hidden" aria-label="Elegir archivos" onChange={(e) => { subir(e.target.files); e.target.value = ''; }} />
        <input ref={camara} type="file" accept="image/*" capture="environment" className="hidden" aria-label="Tomar foto" onChange={(e) => { subir(e.target.files); e.target.value = ''; }} />
      </div>
      {estados.length > 0 && (
        <ul className="space-y-1.5" aria-label="Archivos subiendo">
          {estados.map((e, i) => (
            <li key={`${e.nombre}-${i}`} className="text-sm">
              <div className="flex items-center gap-2">
                <span className="truncate flex-1 min-w-0 text-gray-900">{e.nombre}</span>
                <span className="text-xs text-gray-500">{tamano(e.bytes)}</span>
                <span className={`text-xs font-semibold ${e.estado === 'LISTO' ? 'text-green-700' : e.estado === 'ERROR' ? 'text-red-700' : 'text-amber-800'}`}>
                  {e.estado === 'LISTO' ? 'Subido' : e.estado === 'ERROR' ? 'No se subió' : e.estado === 'SUBIENDO' ? `${e.porCiento} %` : 'En cola'}
                </span>
              </div>
              {e.estado === 'SUBIENDO' && (
                <div className="h-1.5 bg-gray-200 rounded mt-1" role="progressbar" aria-valuenow={e.porCiento} aria-valuemin="0" aria-valuemax="100">
                  <div className="h-1.5 bg-[var(--color-marca)] rounded" style={{ width: `${e.porCiento}%` }} />
                </div>
              )}
              {e.error && <div className="text-xs text-red-700 mt-0.5">{e.error}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
